import dataListTemplate from './data-list.html?raw';

//ToDo:
// remove prop multiple. Should be easier to just have 1 component for each use case.
// Need more accessibility features using live announcement f.ex.

export class DataList extends HTMLElement {
    static get observedAttributes() {
        return [
            'id',
            'multiple',
            'disabled',
            'options',
            'no-options-text',
            'aria-label-text',
            'liveregion-text',
            'options-removable',
        ];
    }

    constructor() {
        super();

        this.handleKeyDown = this.handleKeyDown.bind(this);

        this.options = [];

        this.settings = {
            labelProperty: 'label',
            valueProperty: 'value',
            localization: {
                loading: 'Loading...',
                noOptions: 'No options found',
                removeOption: 'Remove option',
            },
        };
    }

    connectedCallback() {
        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = dataListTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        const datalist = this.shadowRoot.querySelector('.datalist');

        if (!datalist) return;

        this.elementId =
            this.getAttribute('id') ||
            `datalist-${Math.random().toString(36).substring(2, 9)}`;

        if (this.isMultiple) {
            datalist.classList.add('multiple');
            datalist.setAttribute('aria-multiselectable', 'true');
        }

        if (this.isDisabled) {
            datalist.classList.add('disabled');
            datalist.setAttribute('aria-disabled', 'true');
        }

        if (this.hasAttribute('options')) {
            try {
                this.options = JSON.parse(this.getAttribute('options')).map(
                    (opt) =>
                        Object.prototype.hasOwnProperty.call(opt, 'selected')
                            ? opt
                            : { ...opt, selected: false }
                );
            } catch (e) {
                console.error('Invalid JSON in options attribute:', e);
            }
        }

        if (this.hasAttribute('aria-label-text')) {
            datalist.setAttribute(
                'aria-label',
                this.getAttribute('aria-label-text')
            );
        }

        if (this.hasAttribute('liveregion-text')) {
            const liveRegion = this.shadowRoot.querySelector(
                '.datalist-live-region'
            );
            if (liveRegion) {
                liveRegion.textContent = this.getAttribute('liveregion-text');
            }
        }

        if (
            this.hasAttribute('options-removable') &&
            (this.getAttribute('options-removable') === 'true' ||
                this.getAttribute('options-removable') === '')
        ) {
            this.optionsRemovable = true;
        }

        if (this.hasAttribute('no-options-text')) {
            this.settings.localization.noOptions =
                this.getAttribute('no-options-text');
        }

        if (this.hasAttribute('loading-text')) {
            this.settings.localization.loading =
                this.getAttribute('loading-text');
        }

        this.addEventListeners();

        this.updateList();
    }

    addEventListeners() {
        const datalist = this.shadowRoot.querySelector('.datalist');

        datalist.addEventListener('focusin', (event) => {
            if (event.target.classList.contains('option')) {
                event.target.classList.add('focused');
            }
        });

        datalist.addEventListener('focusout', (event) => {
            if (event.target.classList.contains('option')) {
                event.target.classList.remove('focused');
            }
        });
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;
        if (!this.shadowRoot) return;

        const datalist = this.shadowRoot.querySelector('.datalist');

        switch (name) {
            case 'id':
                this.elementId = newValue || this.elementId;
                break;
            case 'multiple': {
                const isMultiple = newValue !== null && newValue !== 'false';
                datalist.classList.toggle('multiple', isMultiple);
                datalist.setAttribute(
                    'aria-multiselectable',
                    isMultiple ? 'true' : 'false'
                );
                if (this.shadowRoot) {
                    this.updateList();
                }
                break;
            }
            case 'disabled': {
                const isDisabled = newValue !== null && newValue !== 'false';
                datalist.classList.toggle('disabled', isDisabled);
                datalist.setAttribute(
                    'aria-disabled',
                    isDisabled ? 'true' : 'false'
                );
                this.updateDisabled();
                break;
            }
            case 'options':
                try {
                    this.options = JSON.parse(newValue).map((opt) =>
                        Object.prototype.hasOwnProperty.call(opt, 'selected')
                            ? opt
                            : { ...opt, selected: false }
                    );
                    this.updateList();
                } catch (e) {
                    console.error('Invalid JSON in options attribute:', e);
                }
                break;
            case 'no-options-text':
                this.settings.localization.noOptions = newValue;
                break;
            case 'aria-label-text':
                if (datalist) {
                    datalist.setAttribute(
                        'aria-label',
                        newValue || 'Select options'
                    );
                }
                break;
            case 'liveregion-text': {
                const liveRegion = this.shadowRoot.querySelector(
                    '.datalist-live-region'
                );
                if (liveRegion) {
                    liveRegion.textContent = newValue || '';
                }
                break;
            }
            case 'options-removable':
                this.optionsRemovable =
                    newValue !== null && newValue !== 'false';
                this.updateList();
                break;
        }
    }

    get isDisabled() {
        return (
            this.hasAttribute('disabled') &&
            this.getAttribute('disabled') !== 'false'
        );
    }

    get isLoading() {
        return (
            this.hasAttribute('loading') &&
            this.getAttribute('loading') !== 'false'
        );
    }

    get isMultiple() {
        return (
            this.hasAttribute('multiple') &&
            this.getAttribute('multiple') !== 'false'
        );
    }

    get isRemovable() {
        return (
            this.hasAttribute('options-removable') &&
            this.getAttribute('options-removable') !== 'false'
        );
    }

    getOptions() {
        return this.options;
    }

    setOptions(newOptions) {
        this.options = Array.isArray(newOptions)
            ? newOptions.map((opt) =>
                  Object.prototype.hasOwnProperty.call(opt, 'selected')
                      ? opt
                      : { ...opt, selected: false }
              )
            : [];

        this.updateList();
    }

    getSelected() {
        return this.options.filter((opt) => opt.selected);
    }

    setValue(newValue, skipEvent = false) {
        const option = this.options.find((opt) => opt.value === newValue);
        if (option) {
            this.selectOption(
                option[this.settings.valueProperty ?? 'value'],
                option[this.settings.labelProperty ?? 'label'],
                skipEvent
            );
        } else {
            const newOption = {
                [this.settings.valueProperty ?? 'value']: newValue,
                [this.settings.labelProperty ?? 'label']: newValue,
            };
            this.addOption(newOption);
            this.selectOption(
                newOption[this.settings.valueProperty ?? 'value'],
                newOption[this.settings.labelProperty ?? 'label'],
                skipEvent
            );
            this.updateListWithNewOption(newOption);
        }
    }

    selectOption(value, label, skipEvent = false) {
        if (skipEvent) return;

        this.dispatchEvent(
            new CustomEvent('datalist:change', {
                detail: { option: { value, label } },
                bubbles: true,
            })
        );
    }

    addOption(option) {
        this.options = [
            ...this.options,
            Object.prototype.hasOwnProperty.call(option, 'selected')
                ? option
                : { ...option, selected: false },
        ];
        this.updateListWithNewOption(option);
        this.updateListAriaSetSize();
    }

    removeOption(option) {
        this.options = this.options.filter((opt) => opt !== option);
    }

    clear(silent = true) {
        this.options.forEach((opt) => {
            opt.selected = false;
        });

        this.updateList();

        if (silent) return;

        this.selectionChanged();
    }

    createNoOptionsOption() {
        const noOptionsElement = document.createElement('li');
        noOptionsElement.className = 'no-options';
        noOptionsElement.textContent =
            this.settings.localization?.noOptions || 'No options found';
        noOptionsElement.setAttribute('role', 'status');
        noOptionsElement.setAttribute('aria-live', 'polite');
        return noOptionsElement;
    }

    createLoadingOption() {
        const loadingElement = document.createElement('li');
        loadingElement.className = 'loading';

        const loadingSpinner = document.createElement('span');
        loadingSpinner.className = 'spinner';
        loadingElement.appendChild(loadingSpinner);

        const loadingSpan = document.createElement('span');
        loadingSpan.textContent =
            this.settings.localization?.loading || 'Loading...';
        loadingElement.appendChild(loadingSpan);

        loadingElement.setAttribute('role', 'status');
        loadingElement.setAttribute('aria-live', 'polite');

        return loadingElement;
    }

    createOption(option, index) {
        const optionElement = document.createElement('li');
        optionElement.className = 'option';
        optionElement.dataset.value = option.value;

        const isSelected = option.selected === true;

        optionElement.setAttribute('id', `${this.elementId}-option-${index}`);
        optionElement.setAttribute('role', 'option');
        optionElement.setAttribute('aria-posinset', index + 1);
        optionElement.setAttribute('aria-setsize', this.options.length);
        optionElement.setAttribute(
            'aria-selected',
            isSelected ? 'true' : 'false'
        );

        optionElement.setAttribute('tabindex', '-1');

        if (this.isDisabled) {
            optionElement.setAttribute('aria-disabled', 'true');
        }

        optionElement.setAttribute(
            'disabled',
            this.isDisabled ? 'true' : 'false'
        );

        if (isSelected) {
            optionElement.classList.add('selected');
        }

        optionElement.addEventListener('keydown', (e) => {
            this.handleKeyDown(e, index);
        });

        optionElement.addEventListener('click', (e) => {
            if (e.target.classList.contains('remove-btn')) return;
            if (this.isDisabled) return;

            this.optionClicked(option.value);
        });

        if (this.isMultiple) {
            const checkbox = document.createElement('span');
            checkbox.className = 'checkbox';
            checkbox.setAttribute('aria-hidden', 'true');

            optionElement.appendChild(checkbox);
        }

        const label = document.createElement('span');
        label.textContent = option.label;

        optionElement.appendChild(label);

        if (this.optionsRemovable) {
            const removeBtn = document.createElement('button');
            removeBtn.className = 'remove-btn';

            if (this.settings.localization?.removeOption) {
                removeBtn.setAttribute(
                    'aria-label',
                    this.settings.localization.removeOption.replace(
                        '{option}',
                        option[this.settings.labelProperty ?? 'label']
                    )
                );
            } else {
                removeBtn.setAttribute(
                    'aria-label',
                    `Remove ${option[this.settings.labelProperty ?? 'label']}`
                );
            }
            removeBtn.innerHTML = '&times;';
            removeBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeOption(option);
                this.updateList();

                this.emit('option-removed', option);
            });
            optionElement.appendChild(removeBtn);
        }

        return optionElement;
    }

    updateList() {
        const datalist = this.shadowRoot.querySelector('.datalist');

        if (!datalist) return;

        datalist.replaceChildren();

        if (this.isLoading) {
            datalist.setAttribute('aria-busy', 'true');
            datalist.appendChild(this.createLoadingOption());
            return;
        }

        if (
            this.hasAttribute('aria-busy') &&
            this.getAttribute('aria-busy') !== 'false'
        ) {
            datalist.removeAttribute('aria-busy');
        }

        if (this.options.length === 0) {
            datalist.appendChild(this.createNoOptionsOption());
            return;
        }

        this.options.forEach((option, index) => {
            const optionElement = this.createOption(option, index);
            datalist.appendChild(optionElement);
        });

        const firstSelectedOption = datalist.querySelector(
            '.option[aria-selected="true"]:not(.disabled)'
        );

        if (firstSelectedOption) {
            firstSelectedOption.setAttribute('tabindex', '0');
        } else {
            const optionsWithTabIndexZero = datalist.querySelectorAll(
                '.option[tabindex="0"]:not(.disabled)'
            );
            if (optionsWithTabIndexZero.length === 0) {
                const firstOption = datalist.querySelector('.option');
                if (firstOption) {
                    firstOption.setAttribute('tabindex', '0');
                }
            }
        }
    }

    updateListWithNewOption(option) {
        const datalist = this.shadowRoot.querySelector('.datalist');

        if (!datalist) return;

        const optionElement = this.createOption(
            option,
            this.options.length - 1
        );

        datalist.appendChild(optionElement);
    }

    updateSelected() {
        const datalist = this.shadowRoot.querySelector('.datalist');

        if (!datalist) return;

        const options = datalist.querySelectorAll('.option');

        options.forEach((opt) => {
            const isSelected = this.selectedValue.includes(opt.dataset.value);
            opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            opt.classList.toggle('selected', isSelected);
        });
    }

    updateListAriaSetSize() {
        const options = this.shadowRoot.querySelectorAll('.option');
        options.forEach((opt) => {
            opt.setAttribute('aria-setsize', this.options.length);
        });
    }

    updateDisabled() {
        const datalist = this.shadowRoot.querySelector('.datalist');

        if (!datalist) return;

        datalist.classList.toggle('disabled', this.isDisabled);
        datalist.setAttribute(
            'aria-disabled',
            this.isDisabled ? 'true' : 'false'
        );

        if (this.isMultiple) {
            const options = datalist.querySelectorAll('.option');
            options.forEach((option) => {
                option.setAttribute(
                    'aria-disabled',
                    this.isDisabled ? 'true' : 'false'
                );
                option.tabIndex = this.isDisabled ? -1 : 0;
            });
        }
    }

    handleKeyDown(event, currentIndex) {
        const options = this.shadowRoot.querySelectorAll('.option');

        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const nextIndex = (currentIndex + 1) % options.length;
                options[currentIndex].classList.remove('focused');
                options[currentIndex].setAttribute('tabindex', '-1');
                options[nextIndex].classList.add('focused');
                options[nextIndex].setAttribute('tabindex', '0');
                options[nextIndex].focus();
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prevIndex =
                    (currentIndex - 1 + options.length) % options.length;
                options[currentIndex].classList.remove('focused');
                options[currentIndex].setAttribute('tabindex', '-1');
                options[prevIndex].classList.add('focused');
                options[prevIndex].setAttribute('tabindex', '0');
                options[prevIndex].focus();
                break;
            }
            case ' ':
            case 'Enter':
                event.preventDefault();
                event.target.click();
                break;
            case 'Escape':
                break;
        }
    }

    optionClicked(value) {
        const option = this.options.find((opt) => opt.value === value);

        if (!option) return;

        const { selected } = option || {};

        if (!this.isMultiple) {
            this.options.forEach((opt) => {
                opt.selected = opt.value === value;
            });

            const optionsElements = this.shadowRoot.querySelectorAll('.option');
            optionsElements.forEach((optEl) => {
                if (optEl.dataset.value !== value) {
                    optEl.classList.remove('selected');
                    optEl.setAttribute('aria-selected', 'false');
                    optEl.setAttribute('tabindex', '-1');
                } else {
                    if (!optEl.classList.contains('selected')) {
                        optEl.classList.add('selected');
                    }
                    optEl.setAttribute('aria-selected', 'true');
                }
            });
        } else {
            option.selected = !selected;

            const optionElement = this.shadowRoot.querySelector(
                `.option[data-value="${value}"]`
            );

            if (optionElement) {
                if (
                    option.selected &&
                    !optionElement.classList.contains('selected')
                ) {
                    optionElement.classList.add('selected');
                } else if (
                    !option.selected &&
                    optionElement.classList.contains('selected')
                ) {
                    optionElement.classList.remove('selected');
                }

                optionElement.setAttribute(
                    'aria-selected',
                    option.selected ? 'true' : 'false'
                );
            }
        }

        this.selectionChanged();
    }

    selectionChanged() {
        this.emit('selection-changed', {
            options: this.options,
        });
    }

    emit(type, detail = {}) {
        let event = new CustomEvent(`datalist:${type}`, {
            bubbles: true,
            cancelable: true,
            detail: detail,
        });

        return this.dispatchEvent(event);
    }

    navigate(direction) {
        const datalist = this.shadowRoot.querySelector('.datalist');
        const options = this.shadowRoot.querySelectorAll(
            '.option:not(.disabled)'
        );

        let currentIndex = -1;
        options.forEach((opt, index) => {
            if (opt.classList.contains('focused')) {
                if (currentIndex === -1) {
                    currentIndex = index;
                }
                opt.classList.remove('focused');
            }
        });

        if (currentIndex === -1) {
            if (options.length > 0) {
                options[0].classList.add('focused');
                options[0].focus();
                const id = options[0].getAttribute('id');
                datalist.setAttribute('aria-activedescendant', id);
            }
        } else {
            let newIndex;
            if (direction === 'down') {
                newIndex = (currentIndex + 1) % options.length;
            } else if (direction === 'up') {
                newIndex = (currentIndex - 1 + options.length) % options.length;
            }

            if (newIndex !== undefined) {
                options[newIndex].classList.add('focused');
                const id = options[newIndex].getAttribute('id');
                datalist.setAttribute('aria-activedescendant', id);
                options[newIndex].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    }
}

customElements.define('svea-datalist', DataList);
